import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Phone, User, Rocket, Target } from 'lucide-react';

const About: React.FC = () => {
    const [showDetails, setShowDetails] = useState(false);

    return (
        <section id="about" className="py-20 bg-white overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row items-center gap-16">

                    {/* Image / Video Placeholder */}
                    <div className="lg:w-1/2 relative">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative z-10"
                        >
                            {/* Main Video */}
                            <div className="rounded-[40px] overflow-hidden rounded-tl-none shadow-2xl relative bg-slate-900 aspect-[4/3]">
                                <video
                                    src="/about-video.mp4"
                                    className="w-full h-full object-cover"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="auto"
                                />
                            </div>

                            {/* Floating Stats Card */}
                            <div className="absolute -bottom-10 -right-10 bg-white p-6 rounded-2xl shadow-xl border-l-4 border-blue-600 max-w-xs hidden md:block">
                                <div className="flex items-center space-x-4">
                                    <div className="text-4xl font-bold text-blue-600">6,561+</div>
                                    <div className="text-gray-600 text-sm font-medium">Clients Satisfaits</div>
                                </div>
                            </div>

                            {/* Decorative Blob */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-100 rounded-full -z-10 blur-3xl opacity-50"></div>
                        </motion.div>
                    </div>

                    {/* Content */}
                    <div className="lg:w-1/2">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h4 className="text-blue-600 font-semibold tracking-wide uppercase mb-2">À propos de FlashCv</h4>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                Boostez votre carrière avec <br /> le CV parfait
                            </h2>
                            <p className="text-gray-600 mb-8 leading-relaxed">
                                FlashCv est conçu pour aider les chercheurs d'emploi à créer des CV percutants et professionnels en quelques minutes, augmentant ainsi leurs chances de décrocher un entretien.
                            </p>

                            <ul className="space-y-4 mb-10">
                                {[
                                    "Design et identité professionnelle",
                                    "Modèles optimisés pour les ATS",
                                    "Téléchargements illimités"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center space-x-3">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                            <Check size={14} strokeWidth={3} />
                                        </div>
                                        <span className="text-gray-700 font-medium">{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex items-center space-x-8">
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
                                >
                                    {showDetails ? "Fermer" : "En savoir plus"}
                                </button>
                                <div className="flex items-center space-x-3">
                                    <div className="w-12 h-12 rounded-full border border-blue-200 flex items-center justify-center text-blue-600">
                                        <Phone size={20} />
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Appelez-nous</div>
                                        <div className="font-bold text-gray-900">+225 07 10 14 59 75</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Detailed Content */}
                <AnimatePresence>
                    {showDetails && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-16 bg-gray-50 rounded-3xl p-8 lg:p-12 border border-gray-100"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div>
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                                            <Rocket size={24} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900">Le Projet FlashCv</h3>
                                    </div>
                                    <p className="text-gray-600 mb-6 leading-relaxed">
                                        FlashCv est né d'un constat simple : des milliers de talents voient leurs opportunités s'envoler à cause d'un CV mal structuré ou peu attrayant. Notre plateforme utilise des technologies de pointe pour démocratiser l'accès à un design professionnel.
                                    </p>

                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                                            <Target size={24} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900">Notre Vision</h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed">
                                        Nous ne nous contentons pas de créer des CV. Notre mission est de devenir l'outil numéro 1 d'insertion professionnelle en Afrique, en fournissant les clés de la réussite à chaque candidat, peu importe son secteur d'activité.
                                    </p>
                                </div>

                                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                                            <User size={24} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900">Le Fondateur</h3>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-6 ">
                                        <div className="w-32 h-32 flex-shrink-0 bg-blue-100 rounded-2xl overflow-hidden border-2 border-white shadow-md">
                                            {/* Placeholder for Founder's Image */}
                                            <div className="w-full h-full flex items-center justify-center text-blue-300">
                                                <User size={48} />
                                            </div>
                                            {/* <img src="/asaph.jpg" alt="Fondateur" className="w-full h-full object-cover" /> */}
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-blue-600 mb-1">Asaph</h4>
                                            <p className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-tighter">Passionné Tech & Innovation</p>
                                            <p className="text-gray-600 italic leading-relaxed">
                                                "Mon ambition est de lever les barrières technologiques qui freinent les chercheurs d'emploi. FlashCv est le premier pas vers un écosystème de recrutement transparent et équitable pour tous."
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Brands Trust Us - Simple strip below */}
                <div className="mt-20 pt-10 border-t border-gray-100">
                    <p className="text-center text-gray-400 text-sm font-semibold mb-8 uppercase tracking-widest">Ils nous font confiance</p>
                    <div className="flex justify-between items-center opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                        {/* Placeholders for logos (Envato, etc.) */}
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="text-2xl font-bold text-gray-300 font-sans">envato</div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
